import React, { useEffect, useRef } from "react";
import { useCursor } from "../cursor/useCursor";
import useGithubProjects from "../../../hooks/useGithubProjects";
import useGithubContributors from "../../../hooks/useGithubContributors";
import useGithubLanguages from "../../../hooks/useGithubLanguages";
import { CURSORS } from "../cursor/cursors";
import { mockMap } from "../../data"

const GITHUB_USERNAME = "badu01";

const CARD_SCROLL = 500;
const RECEDE_SCALE = 0.14;
const RECEDE_ROTATE = 3;
const RECEDE_DRIFT_VH = 6;
const TITLE_HOLD = 200;
const END_HOLD = 300;
const LERP = 0.1;



const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
const clamp01 = (v) => Math.min(1, Math.max(0, v));

const CARD_RADIUS_CLASS = "rounded-[65px] md:rounded-[100px] lg:rounded-[135px]";
const CARD_HEIGHT_CLASS = "h-[calc(100vh-2rem)] sm:h-[calc(100vh-2.5rem)] md:h-[calc(100vh-3rem)]";

function timeAgo(dateString) {
  const diffMs = Date.now() - new Date(dateString).getTime();
  const days = Math.floor(diffMs / 86400000);
  if (days < 1) return "today";
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

function ContributorAvatars({ owner, repo }) {
  const { contributors } = useGithubContributors(owner, repo);
  if (!contributors.length) return null;
  const shown = contributors.slice(0, 4);
  const extra = contributors.length - shown.length;

  return (
    <div className="flex items-center -space-x-3">
      {shown.map((c) => (
        <a key={c.login} href={c.html_url} target="_blank" rel="noopener noreferrer" title={`${c.login} · ${c.contributions} commits`} className="h-9 w-9 shrink-0 overflow-hidden rounded-full border-2 border-[#111] bg-[#222] transition-transform duration-200 hover:z-10 hover:scale-110">
          <img src={c.avatar_url} alt={c.login} className="h-full w-full object-cover" />
        </a>
      ))}
      {extra > 0 && (
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-[#111] bg-[#1a1a1a] text-[10px] text-[#999]">+{extra}</div>
      )}
    </div>
  );
}

function LanguageBar({ owner, repo }) {
  const { languages } = useGithubLanguages(owner, repo);
  if (!languages.length) return null;
  const top = languages.slice(0, 5);

  return (
    <div className="mt-6">
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-white/5">
        {top.map((l) => (
          <div key={l.name} style={{ width: `${l.pct}%`, background: l.color }} title={`${l.name} ${l.pct.toFixed(1)}%`} />
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 font-['Space_Mono',monospace] text-[10px] text-[#888]">
        {top.map((l) => (
          <span key={l.name} className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ background: l.color }} />
            {l.name} <span className="text-[#555]">{l.pct.toFixed(0)}%</span>
          </span>
        ))}
      </div>
    </div>
  );
}

const ProjectCard = React.forwardRef(function ProjectCard({ project, index, style }, ref) {
  const description = project.description || "No description provided.";
  const topics = (project.topics || []).filter((t) => t !== "featured" && t !== "portfolio");
  const { cursor } = useCursor();

  return (
    <a href={project.html_url} target="_blank" rel="noopener noreferrer">
      <div
        {...cursor({ ...CURSORS.project, text: "view", background: '', border: '1px solid #fff', blendMode: "difference", size: 250 })}
        ref={ref}
        style={style}
        className={`absolute left-0 right-0 top-4 sm:top-5 md:top-2 mt-2 sm:mt-4 md:mt-6 ${CARD_HEIGHT_CLASS} ${CARD_RADIUS_CLASS} flex flex-col lg:flex-row overflow-hidden border border-white/10 bg-[#111] will-change-transform`}
      >
        {/* LEFT — text */}
        <div className="flex flex-1 flex-col justify-between p-6 sm:p-8 md:p-10 lg:p-12 lg:max-w-[52%]">
          <div>
            <div className="mb-3 flex flex-wrap items-center gap-2 font-minecraft text-[10px] sm:text-[0.65rem] md:text-[0.68rem] tracking-[1px] text-white">
              <span>✦</span>
              <span>{String(index + 1).padStart(2, "0")}</span>
              {project.language && <span className="rounded-full border border-white/20 px-2 py-0.5 text-[#999] normal-case">{project.language}</span>}
              {project.stargazers_count > 0 && <span className="text-[#999] normal-case">★ {project.stargazers_count}</span>}
              <span className="text-[#666] normal-case">· updated {timeAgo(project.pushed_at)}</span>
            </div>

            <div className="mb-5 sm:mb-6 md:mb-7 font-minecraft text-[clamp(1.3rem,3.4vw,4.4rem)] leading-[1.2] tracking-[1px] text-white">
              {project.name}
            </div>

            <p className="max-w-[520px] font-['Space_Mono',monospace] text-[10px] sm:text-[11px] md:text-[0.85rem] leading-[1.7] sm:leading-[1.8] md:leading-[1.85] text-[#999] text-left">
              {description}
            </p>

            {topics.length > 0 && (
              <div className="mt-4 flex flex-wrap max-w-2xl gap-2">
                {topics.map((t) => (
                  <span key={t} className="rounded-full border border-white/15 px-3 py-1 font-['Space_Mono',monospace] text-[11px] text-[#999]">
                    {t}
                  </span>
                ))}
              </div>
            )}

            <LanguageBar owner={project.owner?.login} repo={project.name} />
          </div>

          <div className="mt-6 flex items-center justify-between">
            <ContributorAvatars owner={project.owner?.login} repo={project.name} />
          </div>
        </div>

        {/* RIGHT — mockup, image bottom flush with card bottom */}
        {/* MOCKUP */}
        <div className="relative mt-auto flex w-full shrink-0 items-end justify-center overflow-hidden lg:mt-0 lg:flex-1">
          <div className="relative w-full max-w-[640px]">
            <img
              src={mockMap[project.name]}
              alt={project.name}
              className="relative z-[1] h-80 w-full object-contain object-bottom md:h-auto"
            />
          </div>
        </div>
      </div>
    </a>
  );
});

export default function ProjectContainer() {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const cardsRef = useRef([]);
  const rafRef = useRef(null);
  const smoothScroll = useRef(0);

  const { cursor } = useCursor();
  const { projects, loading } = useGithubProjects(GITHUB_USERNAME, true);

  const ALL_CARDS = projects.length + 1;

  useEffect(() => {
    const section = sectionRef.current;
    const title = titleRef.current;
    if (!section) return;

    const tick = () => {
      const rect = section.getBoundingClientRect();
      const targetScroll = Math.max(0, -rect.top);
      smoothScroll.current += (targetScroll - smoothScroll.current) * LERP;
      const scrolled = smoothScroll.current;

      const titleOpacity = 1 - clamp01((scrolled - TITLE_HOLD) / (CARD_SCROLL * 0.6));
      if (title) {
        title.style.opacity = titleOpacity;
        title.style.transform = `scale(${1 - (1 - titleOpacity) * 0.08})`;
      }

      const progress = cardsRef.current.map((_, i) => {
        const start = TITLE_HOLD + i * CARD_SCROLL;
        return easeOutCubic(clamp01((scrolled - start) / CARD_SCROLL));
      });

      cardsRef.current.forEach((card, i) => {
        if (!card) return;
        const prog = progress[i];
        const recede = i < cardsRef.current.length - 1 ? progress[i + 1] : 0;
        const translateVh = (1 - prog) * 100 - recede * RECEDE_DRIFT_VH;
        const scale = 1 - recede * RECEDE_SCALE;
        const rotate = recede * RECEDE_ROTATE;
        card.style.transform = `translateY(${translateVh}vh) scale(${scale}) rotate(${rotate}deg)`;
      });

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, []);

  const trackHeight = `calc(110vh + ${TITLE_HOLD + ALL_CARDS * CARD_SCROLL + END_HOLD}px)`;

  return (
    <section
      {...cursor({ ...CURSORS.project, blendMode: "difference", background: "#fff" })}
      ref={sectionRef}
      className="relative bg-black px-3 sm:px-4 md:px-6 lg:px-8"
      style={{
        height: trackHeight,
        backgroundImage: `
          linear-gradient(rgba(255,255,255,0.078) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.099) 1px, transparent 1px)
        `,
        backgroundSize: "clamp(90px, 22vw, 210px) clamp(80px, 20vw, 190px)",
      }}
    >
      <div className="relative h-full">
        <div className="sticky top-0 h-screen overflow-hidden">

          <div ref={titleRef} className="absolute inset-0 z-[1] flex flex-col items-center justify-center pointer-events-none will-change-transform px-4">
            <div className="mb-3 font-minecraft text-[10px] sm:text-xs md:text-[0.78rem] tracking-[3px] sm:tracking-[4px] text-[#666]">
              FEATURED
            </div>
            <div className="font-minecraft text-[clamp(2rem,9vw,5rem)] leading-[1.1] tracking-[2px] sm:tracking-[15px] text-center text-white">
              PROJECTS
            </div>
          </div>

          {loading && projects.length === 0 && (
            <div
              ref={(el) => { cardsRef.current[0] = el; }}
              style={{ zIndex: 10, transform: "translateY(100vh)" }}
              className={`absolute left-0 right-0 top-4 sm:top-5 md:top-6 mt-2 sm:mt-4 md:mt-6 ${CARD_HEIGHT_CLASS} ${CARD_RADIUS_CLASS} flex items-center justify-center border border-white/10 bg-[#111] font-minecraft text-sm text-[#666]`}
            >
              loading projects...
            </div>
          )}

          {projects.map((proj, i) => (
            <ProjectCard
              key={proj.id}
              project={proj}
              index={i}
              ref={(el) => { cardsRef.current[i] = el; }}
              style={{ zIndex: 10 + i, transform: "translateY(100vh)" }}
            />
          ))}

          <a
            href={`https://github.com/${GITHUB_USERNAME}?tab=repositories`}
            target="_blank"
            rel="noopener noreferrer"
            ref={(el) => { cardsRef.current[projects.length] = el; }}
            style={{ zIndex: 10 + projects.length, transform: "translateY(100vh)" }}
            className={`absolute left-0 right-0 top-4 sm:top-5 md:top-6 mt-2 sm:mt-4 md:mt-6 ${CARD_HEIGHT_CLASS} ${CARD_RADIUS_CLASS} flex flex-col items-center justify-center p-6 sm:p-8 md:p-10 lg:p-12 cursor-pointer no-underline will-change-transform bg-[rgba(20,20,20,0.55)] backdrop-blur-xl border border-white/15 hover:bg-[rgba(30,30,30,0.65)]`}
          >
            <div className="mb-3 sm:mb-4 font-minecraft text-[10px] sm:text-xs md:text-[0.75rem] tracking-[3px] sm:tracking-[4px] text-[#666]">
              VIEW ALL
            </div>
            <div className="font-minecraft text-[clamp(1.5rem,7vw,3.2rem)] leading-[1.2] tracking-[2px] sm:tracking-[3px] text-center text-white">
              PROJECTS
            </div>
            <div className="mt-6 sm:mt-7 md:mt-8 flex h-12 w-12 sm:h-14 sm:w-14 md:h-[58px] md:w-[58px] items-center justify-center shrink-0 rounded-full border border-white/30 text-base sm:text-lg text-white transition-all duration-200 hover:border-white hover:bg-white hover:text-black">
              ↗
            </div>
          </a>

        </div>
      </div>
    </section>
  );
}